import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Autocomplete,
  TextField,
  Button,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Grid,
  Alert,
  Divider,
  CardActionArea,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Radio,
  Pagination
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import GavelIcon from '@mui/icons-material/Gavel';
import DescriptionIcon from '@mui/icons-material/Description';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import FolderIcon from '@mui/icons-material/Folder';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { getSupremeCourtCases, searchPrecedents } from '../api';
import { apiService } from '../api';

function Precedents() {
  const [supremeCourtCases, setSupremeCourtCases] = useState([]);
  const [filteredCases, setFilteredCases] = useState([]);
  const [myCases, setMyCases] = useState([]);
  const [selectedSupremeCase, setSelectedSupremeCase] = useState(null);
  const [selectedMyCase, setSelectedMyCase] = useState(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingCases, setLoadingCases] = useState(false);
  const [searching, setSearching] = useState(false);
  const [precedents, setPrecedents] = useState([]);
  const [error, setError] = useState('');
  const [searchSummary, setSearchSummary] = useState(null);
  
  // Pagination state
  const [page, setPage] = useState(1);
  const casesPerPage = 40;
  
  // Document selection dialog
  const [documentDialogOpen, setDocumentDialogOpen] = useState(false);
  const [caseDocuments, setCaseDocuments] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [loadingDocuments, setLoadingDocuments] = useState(false);

  useEffect(() => {
    fetchSupremeCourtCases();
    fetchMyCases();
  }, []);

  useEffect(() => {
    // Filter cases based on keyword search
    if (searchKeyword.trim() === '') {
      setFilteredCases(supremeCourtCases);
    } else {
      const keyword = searchKeyword.toLowerCase();
      const filtered = supremeCourtCases.filter(caseItem => {
        const keywords = caseItem.keywords || [];
        return keywords.some(k => k.toLowerCase().includes(keyword));
      });
      setFilteredCases(filtered);
    }
    // Reset to page 1 when search changes
    setPage(1);
  }, [searchKeyword, supremeCourtCases]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredCases.length / casesPerPage);
  const startIndex = (page - 1) * casesPerPage;
  const endIndex = startIndex + casesPerPage;
  const paginatedCases = filteredCases.slice(startIndex, endIndex);

  const handlePageChange = (event, value) => {
    setPage(value);
    // Scroll to top of cases section
    window.scrollTo({ top: 400, behavior: 'smooth' });
  };

  const fetchMyCases = async () => {
    setLoadingCases(true);
    try {
      const response = await apiService.getCases();
      // The cases are in response.data.cases
      const casesData = response.data?.cases || [];
      console.log('Fetched cases:', casesData); // Debug log
      setMyCases(casesData);
    } catch (err) {
      console.error('Failed to load cases:', err);
      setMyCases([]); // Set empty array on error
    } finally {
      setLoadingCases(false);
    }
  };

  const fetchSupremeCourtCases = async () => {
    setLoading(true);
    try {
      const response = await getSupremeCourtCases();
      setSupremeCourtCases(response.cases || []);
    } catch (err) {
      setError('Failed to load Supreme Court cases');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCaseDocuments = async (caseId) => {
    setLoadingDocuments(true);
    try {
      const response = await apiService.getCaseDocuments(caseId);
      setCaseDocuments(response.data?.documents || []);
    } catch (err) {
      console.error('Failed to load documents:', err);
      setError('Failed to load case documents');
      setCaseDocuments([]);
    } finally {
      setLoadingDocuments(false);
    }
  };

  const handleFindPrecedentsClick = async () => {
    if (!selectedMyCase) {
      setError('Please select your case from the top right dropdown');
      return;
    }

    // Fetch documents for the selected case
    await fetchCaseDocuments(selectedMyCase.id);
    setDocumentDialogOpen(true);
    setSelectedDocument(null);
  };

  const handleDocumentSelect = (document) => {
    setSelectedDocument(document);
  };

  const handleSearchWithDocument = async () => {
    setDocumentDialogOpen(false);
    setSearching(true);
    setError('');
    setPrecedents([]);

    try {
      const response = await searchPrecedents({
        my_case: selectedMyCase,
        document_id: selectedDocument?.id,
        document_name: selectedDocument?.name
      });
      setPrecedents(response.precedents || []);
      
      if (!response.precedents || response.precedents.length === 0) {
        setError('No similar cases found');
      }
    } catch (err) {
      setError('Failed to search for precedents');
      console.error(err);
    } finally {
      setSearching(false);
    }
  };

  const handleSearchPrecedents = async () => {
    if (!selectedMyCase) {
      setError('Please select your case from the top right dropdown');
      return;
    }

    setSearching(true);
    setError('');
    setPrecedents([]);

    try {
      const response = await searchPrecedents({
        my_case: selectedMyCase
      });
      
      console.log('Precedent search response:', response);
      console.log('Number of precedents:', response.precedents?.length);
      console.log('Precedents data:', response.precedents);
      
      const precedentsData = response.precedents || [];
      console.log('Setting precedents state with:', precedentsData.length, 'cases');
      
      setPrecedents(precedentsData);
      setSearchSummary({
        totalFound: response.total_found || 0,
        keywords: response.extracted_keywords || [],
        legalIssues: response.legal_issues || [],
        legalAreas: response.legal_areas || []
      });
      
      if (precedentsData.length === 0) {
        setError('No similar cases found. The AI could not identify sufficiently relevant precedents.');
      } else {
        console.log('Successfully set', precedentsData.length, 'precedents');
        // Scroll to results after a short delay to ensure rendering
        setTimeout(() => {
          const resultsElement = document.getElementById('precedent-results');
          if (resultsElement) {
            resultsElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      }
    } catch (err) {
      setError('Failed to search for precedents: ' + (err.message || 'Unknown error'));
      console.error('Precedent search error:', err);
    } finally {
      setSearching(false);
    }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header with My Case Selection */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <GavelIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
          <Typography variant="h4" fontWeight="bold">
            Precedent Search
          </Typography>
        </Box>
        
        {/* My Case Selection - Top Right */}
        <Autocomplete
          options={Array.isArray(myCases) ? myCases : []}
          getOptionLabel={(option) => option.title || option.case_number || 'Untitled Case'}
          value={selectedMyCase}
          onChange={(event, newValue) => setSelectedMyCase(newValue)}
          loading={loadingCases}
          sx={{ width: 400 }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Your Case"
              variant="outlined"
              size="small"
              placeholder="Choose your case..."
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <FolderIcon sx={{ color: 'action.active', mr: 1, ml: 1 }} />
                    {params.InputProps.startAdornment}
                  </>
                ),
                endAdornment: (
                  <>
                    {loadingCases ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </Box>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Select your case from the top-right dropdown, then click "Find Precedents" to let AI search for similar Supreme Court cases automatically.
      </Typography>

      {/* Find Precedents Button - Shows when case is selected */}
      {selectedMyCase && (
        <Paper elevation={3} sx={{ p: 3, mb: 3, bgcolor: 'primary.50' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" color="primary">
                Selected: {selectedMyCase.title || selectedMyCase.case_number}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedMyCase.description}
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="large"
              startIcon={searching ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
              onClick={handleFindPrecedentsClick}
              disabled={searching}
            >
              {searching ? 'Searching...' : 'Find Precedents'}
            </Button>
          </Box>
        </Paper>
      )}

      {/* Search Summary - Shows what AI extracted from user's case */}
      {searchSummary && precedents.length > 0 && (
        <Paper elevation={3} sx={{ p: 3, mb: 3, bgcolor: 'info.50', border: '2px solid', borderColor: 'primary.main' }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', color: 'primary.main', fontWeight: 'bold' }}>
            <SearchIcon sx={{ mr: 1 }} />
            AI Analysis of Your Case
          </Typography>
          
          <Grid container spacing={2}>
            {searchSummary.legalAreas && searchSummary.legalAreas.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Legal Areas:</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                  {searchSummary.legalAreas.map((area, idx) => (
                    <Chip key={idx} label={area} size="small" color="primary" />
                  ))}
                </Box>
              </Grid>
            )}
            
            {searchSummary.legalIssues && searchSummary.legalIssues.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Key Legal Issues Identified:</Typography>
                <Box component="ul" sx={{ mt: 0.5, pl: 2 }}>
                  {searchSummary.legalIssues.slice(0, 3).map((issue, idx) => (
                    <Typography key={idx} component="li" variant="body2" sx={{ mb: 0.5 }}>
                      {issue}
                    </Typography>
                  ))}
                </Box>
              </Grid>
            )}
            
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">
                Search Results: {precedents.length} relevant precedents found (from {searchSummary.totalFound} initial matches)
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Keyword Search - For browsing Supreme Court cases (hidden when precedents are shown) */}
      {precedents.length === 0 && (
        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <AccountBalanceIcon sx={{ mr: 1 }} />
            Browse Supreme Court Cases
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            You can browse Supreme Court cases here, but AI will automatically find the most relevant precedents when you click "Find Precedents" above.
          </Typography>
        
        <TextField
          fullWidth
          label="Search by Keywords"
          variant="outlined"
          placeholder="Type keywords to filter cases (e.g., constitutional, fundamental rights, criminal)..."
          value={searchKeyword}
          onChange={(e) => setSearchKeyword(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1 }} />
          }}
          sx={{ mb: 3 }}
        />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {filteredCases.length} case(s) found {searchKeyword && `for "${searchKeyword}"`} - Showing {startIndex + 1}-{Math.min(endIndex, filteredCases.length)} of {filteredCases.length}
          </Typography>
          {totalPages > 1 && (
            <Pagination 
              count={totalPages} 
              page={page} 
              onChange={handlePageChange}
              color="primary"
              size="small"
            />
          )}
        </Box>

        {/* Supreme Court Cases Grid */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2}>
            {paginatedCases.map((caseItem, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card 
                  elevation={1}
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.2s',
                    '&:hover': {
                      boxShadow: 3,
                      transform: 'translateY(-2px)'
                    }
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, width: '100%' }}>
                    <Typography variant="subtitle1" color="primary" sx={{ mb: 1, fontWeight: 600, lineHeight: 1.3 }}>
                      {caseItem.case_name}
                    </Typography>

                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      sx={{ 
                        mb: 2,
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        minHeight: '3.6em'
                      }}
                    >
                      {caseItem.case_summary || 'No summary available'}
                    </Typography>

                    {caseItem.keywords && caseItem.keywords.length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                        {caseItem.keywords.slice(0, 5).map((keyword, idx) => (
                          <Chip
                            key={idx}
                            label={keyword}
                            size="small"
                            variant="outlined"
                            color={searchKeyword && keyword.toLowerCase().includes(searchKeyword.toLowerCase()) ? 'primary' : 'default'}
                            sx={{ fontSize: '0.7rem', height: '20px' }}
                          />
                        ))}
                        {caseItem.keywords.length > 5 && (
                          <Chip
                            label={`+${caseItem.keywords.length - 5}`}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: '20px' }}
                          />
                        )}
                      </Box>
                    )}

                    <Divider sx={{ my: 1 }} />

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {caseItem.judge && (
                        <Typography variant="caption" color="text.secondary" noWrap>
                          <strong>Judge:</strong> {caseItem.judge}
                        </Typography>
                      )}
                      {caseItem.verdict_date && (
                        <Typography variant="caption" color="text.secondary">
                          <strong>Date:</strong> {caseItem.verdict_date}
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Bottom Pagination */}
        {!loading && totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination 
              count={totalPages} 
              page={page} 
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
        </Paper>
      )}

      {/* Error Message */}
      {error && (
        <Alert severity="warning" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Results */}
      {precedents.length > 0 && (
        <Box id="precedent-results" sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" fontWeight="bold">
              AI-Recommended Precedents ({precedents.length})
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setPrecedents([]);
                setSearchSummary(null);
                setError('');
              }}
            >
              Clear Results & Browse All Cases
            </Button>
          </Box>
          
          <Grid container spacing={3}>
            {precedents.map((precedent, index) => (
              <Grid item xs={12} key={index}>
                <Card elevation={2} sx={{ 
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                  }
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography variant="h6" color="primary" sx={{ flex: 1 }}>
                        {precedent.case_name}
                      </Typography>
                      <Chip 
                        label={`${(precedent.similarity_score * 100).toFixed(1)}% Match`}
                        color="primary"
                        size="small"
                      />
                    </Box>

                    <Divider sx={{ mb: 2 }} />

                    <Typography variant="body2" paragraph>
                      <strong>Summary:</strong> {precedent.summary}
                    </Typography>

                    {precedent.judge && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        <strong>Judge:</strong> {precedent.judge}
                      </Typography>
                    )}

                    {precedent.verdict_date && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        <strong>Date:</strong> {precedent.verdict_date}
                      </Typography>
                    )}

                    {/* Show matched legal areas */}
                    {precedent.matched_areas && precedent.matched_areas.length > 0 && (
                      <Box sx={{ mt: 2, mb: 2 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Legal Areas:</strong>
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {precedent.matched_areas.map((area, idx) => (
                            <Chip
                              key={idx}
                              label={area}
                              size="small"
                              color="secondary"
                              variant="filled"
                            />
                          ))}
                        </Box>
                      </Box>
                    )}

                    {/* Show matched legal phrases */}
                    {precedent.matched_phrases && precedent.matched_phrases.length > 0 && (
                      <Box sx={{ mt: 2, mb: 2 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Matched Legal Concepts:</strong>
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {precedent.matched_phrases.map((phrase, idx) => (
                            <Chip
                              key={idx}
                              label={phrase}
                              size="small"
                              color="primary"
                              variant="filled"
                            />
                          ))}
                        </Box>
                      </Box>
                    )}

                    {/* Show matched terms */}
                    {precedent.matched_terms && precedent.matched_terms.length > 0 && (
                      <Box sx={{ mt: 2, mb: 2 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Matched Terms:</strong>
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {precedent.matched_terms.slice(0, 8).map((term, idx) => (
                            <Chip
                              key={idx}
                              label={term}
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      </Box>
                    )}

                    {precedent.keywords && precedent.keywords.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>All Keywords:</strong>
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {precedent.keywords.map((keyword, idx) => (
                            <Chip
                              key={idx}
                              label={keyword}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      </Box>
                    )}

                    {/* Source and View Case Button */}
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                      {precedent.source_file && (
                        <Typography variant="caption" color="text.secondary">
                          Source: {precedent.source_file}
                          {precedent.page_number && ` (Page ${precedent.page_number})`}
                        </Typography>
                      )}
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<DescriptionIcon />}
                        onClick={() => {
                          // Open PDF - use backend URL
                          const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:5002';
                          const pdfPath = `${backendUrl}/SUPREME_COURT_REPORTS/${precedent.source_file}`;
                          window.open(pdfPath, '_blank');
                        }}
                        sx={{ ml: 2 }}
                      >
                        View Case
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Document Selection Dialog */}
      <Dialog 
        open={documentDialogOpen} 
        onClose={() => setDocumentDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <DescriptionIcon sx={{ mr: 1 }} />
            Select Document for Precedent Search
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose a document from this case to use for finding similar precedents. The AI will analyze the document content.
          </Typography>

          {loadingDocuments ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : caseDocuments.length === 0 ? (
            <Alert severity="warning">
              No documents found for this case. Please upload documents first.
            </Alert>
          ) : (
            <List>
              {caseDocuments.map((doc) => (
                <ListItem key={doc.id} disablePadding>
                  <ListItemButton 
                    onClick={() => handleDocumentSelect(doc)}
                    selected={selectedDocument?.id === doc.id}
                    sx={{
                      '&.Mui-selected': {
                        backgroundColor: 'primary.light',
                        '&:hover': {
                          backgroundColor: 'primary.light',
                        }
                      }
                    }}
                  >
                    <ListItemIcon>
                      <Radio
                        checked={selectedDocument?.id === doc.id}
                        onChange={() => handleDocumentSelect(doc)}
                      />
                    </ListItemIcon>
                    <ListItemIcon>
                      <InsertDriveFileIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={doc.filename || doc.name || 'Untitled Document'}
                      secondary={doc.file_type ? `Type: ${doc.file_type.toUpperCase()}` : null}
                      primaryTypographyProps={{ 
                        color: 'text.primary',
                        fontWeight: 500 
                      }}
                      secondaryTypographyProps={{ 
                        color: 'text.secondary',
                        fontSize: '0.875rem'
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDocumentDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSearchWithDocument}
            variant="contained"
            disabled={!selectedDocument || loadingDocuments}
            startIcon={<SearchIcon />}
          >
            Search Precedents
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Precedents;
